import EURegion from 'src/assets/models/eu-region.svg';
import GlobalRegion from 'src/assets/models/global-region.svg';
import USRegion from 'src/assets/models/us-region.svg';
import { IS_MTP_DEPLOYMENT } from 'src/config';
import type { UPLOAD_TYPE } from 'src/types/enum';
import {
  ACCOUNT_TYPE,
  FILE_EXTENSIONS,
  RESOURCE_TYPE,
  SUPPORTED_UPLOAD_FILES_FORMAT
} from 'src/types/enum';

export const MAX_NAME_LENGTH = 200;
export const PERSONA_INSTRUCTIONS_MAX_LENGTH = 12000;
export const AUDIO_CHUNK_DURATION = 10000;
export const BYTES_PER_MB = 1024 * 1024;
export const MAX_FILENAME_DISPLAY_LENGTH = 15;
export const DEFAULT_MEDIA_MAX_SIZE_MB = 400; // matches FILE_EXTENSIONS media defaults in fileSizeLimitsDefaults.ts
export const TWENTY_FOUR_HOUR_FORMAT = 'dd MMM yyyy HH:mm';
export const DATE_FORMAT = 'MMMM dd yyyy';
export const DATE_FILTER_FORMAT = 'yyyy-MM-dd';
export const TIME_FORMAT = 'hh:mm';
export const ANALYTICS_DATE_FORMAT = 'd MMM, yyyy';
export const MONTH_YEAR_FORMAT = 'MMM, yyyy';
export const DOCUMENT_FORMAT = 'EEEE dd/MM/yyyy hh:mm a';
export const EMAIL_REG_EXP =
  /^[a-z0-9%]+([.\-][a-z0-9%]+)*@([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;
export const SPECIAL_CHAR_PATTERN_REGEX = /[^a-z0-9._@\-]/;
export const DOMAIN_REG_EXP =
  /^(?!-)(?:[a-zA-Z0-9-]{1,63}(?<!-)\.)+[a-zA-Z]{2,}$/;
export const VALID_NUMBER_REG_EXP = /^[1-9]\d*$/;
export const VALID_SUBDOMAIN_REGEX =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)$/;
export const MAX_VISIBLE_COUNT_CHATSTEPS_CITATIONS = 2;
export const VALID_NAME_REGEXP = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
export const MAX_SPREADSHEET_FILE_SIZE_IN_MB = 50; // upload file datalibrary
export const MAX_MEDIA_FILE_SIZE_IN_MB = 400; // upload file datalibrary
export const MAX_DOCUMENT_FILE_SIZE_IN_MB = 300; // upload file datalibrary

// Image Generation Constants
export const IMAGE_TRANSITION_TIMEOUT = 200; // Duration in ms for image transition/fade animations

export const FILE_FORMAT = {
  [SUPPORTED_UPLOAD_FILES_FORMAT.PPT]: ['.'.concat(FILE_EXTENSIONS.PPT)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.PPTX]: ['.'.concat(FILE_EXTENSIONS.PPTX)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.PDF]: ['.'.concat(FILE_EXTENSIONS.PDF)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.DOC]: ['.'.concat(FILE_EXTENSIONS.DOC)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.DOCX]: ['.'.concat(FILE_EXTENSIONS.DOCX)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.XLS]: ['.'.concat(FILE_EXTENSIONS.XLS)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.XLSX]: ['.'.concat(FILE_EXTENSIONS.XLSX)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.CSV]: ['.'.concat(FILE_EXTENSIONS.CSV)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.ODS]: ['.'.concat(FILE_EXTENSIONS.ODS)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.ODP]: ['.'.concat(FILE_EXTENSIONS.ODP)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.ODT]: ['.'.concat(FILE_EXTENSIONS.ODT)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.MP3]: ['.'.concat(FILE_EXTENSIONS.MP3)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.WAV]: ['.'.concat(FILE_EXTENSIONS.WAV)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.M4A]: ['.'.concat(FILE_EXTENSIONS.M4A)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.MPEG]: ['.'.concat(FILE_EXTENSIONS.MPEG)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.WEBM]: ['.'.concat(FILE_EXTENSIONS.WEBM)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.MP4]: ['.'.concat(FILE_EXTENSIONS.MP4)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.MOV]: ['.'.concat(FILE_EXTENSIONS.MOV)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.M4V]: ['.'.concat(FILE_EXTENSIONS.M4V)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.THREE_GP]: [
    '.'.concat(FILE_EXTENSIONS.THREE_GP)
  ],
  [SUPPORTED_UPLOAD_FILES_FORMAT.AAC]: ['.'.concat(FILE_EXTENSIONS.AAC)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.CAF]: ['.'.concat(FILE_EXTENSIONS.CAF)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.AMR]: ['.'.concat(FILE_EXTENSIONS.AMR)],
  [SUPPORTED_UPLOAD_FILES_FORMAT.TXT]: ['.'.concat(FILE_EXTENSIONS.TXT)]
};

export const ROUTES = {
  NAMESPACE_ROUTE: 'workspaces',
  WORKSPACE_ROUTE: 'collections',
  RESOURCES_ROUTE: 'resources'
};

export const DATA_LIBRARY_ROUTES = {
  [RESOURCE_TYPE.NAMESPACE]: ROUTES.NAMESPACE_ROUTE,
  [RESOURCE_TYPE.WORKSPACE]: ROUTES.WORKSPACE_ROUTE,
  [RESOURCE_TYPE.PUBLIC_WORKSPACE]: ROUTES.WORKSPACE_ROUTE,
  [RESOURCE_TYPE.PRIVATE_WORKSPACE]: ROUTES.WORKSPACE_ROUTE,
  [RESOURCE_TYPE.FOUND_WORKSPACE]: ROUTES.WORKSPACE_ROUTE,
  [RESOURCE_TYPE.COLLECTION]: ROUTES.RESOURCES_ROUTE,
  [RESOURCE_TYPE.FOUND_COLLECTION]: ROUTES.RESOURCES_ROUTE,
  [RESOURCE_TYPE.FOLDER]: ROUTES.RESOURCES_ROUTE,
  [RESOURCE_TYPE.FILE]: ROUTES.RESOURCES_ROUTE
};

export const SERVER_DOWN_CODES = [502, 503];

export const PUBLIC_ROUTES = [
  '/signup',
  '/privacypolicy',
  '/sign-up',
  ...(IS_MTP_DEPLOYMENT ? ['/'] : [])
];

export const USER_ROUTES = {
  USERS: 'users',
  USER_GROUPS: 'user-groups',
  ROLES: 'roles',
  USER_ROLES: 'user-roles'
};

export const ANALYTICS_ROUTES = {
  USAGE: '/usage',
  INTERNAL_ANALYTICS: '/internal-analytics',
  CHAT: '/chat-analytics'
};

export const VALID_CHAT_FILES_EXTENSION =
  '.docx, .doc, .pptx, .ppt, .xlsx, .xls, .csv, .odt, .odp, .ods, .pdf, .dot, .rtf, .mp3, .mp4, .mpga, .mpeg, .wav, .m4a, .webm, .mov, .m4v, .3gp, .aac, .caf, .amr, .txt';

// Dot-less allowlist for extension-checking consumers (e.g. validateUploadFiles).
// VALID_CHAT_FILES_EXTENSION above stays string-shaped for the HTML accept= attribute.
export const VALID_CHAT_FILE_EXTENSIONS = [
  'docx',
  'doc',
  'pptx',
  'ppt',
  'xlsx',
  'xls',
  'csv',
  'odt',
  'odp',
  'ods',
  'pdf',
  'dot',
  'rtf',
  'mp3',
  'mp4',
  'mpga',
  'mpeg',
  'wav',
  'm4a',
  'webm',
  'mov',
  'm4v',
  '3gp',
  'aac',
  'caf',
  'amr',
  'txt',
  'png',
  'jpeg',
  'jpg',
  'gif',
  'svg'
];

export const VALID_CHAT_DATA_ANALYST_FILES_EXTENSION = '.xlsx, .xls, .csv';

export const VALID_AUDIO_VIDEO_EXTENSIONS = [
  'mp3',
  'mp4',
  'mpga',
  'mpeg',
  'wav',
  'm4a',
  'webm',
  'mov',
  'm4v',
  '3gp',
  'aac',
  'caf',
  'amr'
];

export const VALID_CHAT_IMAGE_FILES_EXTENSION = '.png, .jpeg, .jpg, .gif';

export const VALID_ASSISTANT_FILES_EXTENSION =
  '.docx, .doc, .pptx, .ppt, .xlsx, .xls, .csv, .odt, .odp, .ods, .pdf, .dot, .rtf, .txt';

// Dot-less allowlist for extension-checking consumers (e.g. validateUploadFiles).
// VALID_ASSISTANT_FILES_EXTENSION above stays string-shaped for the HTML accept= attribute.
export const VALID_ASSISTANT_FILE_EXTENSIONS = [
  'docx',
  'doc',
  'pptx',
  'ppt',
  'xlsx',
  'xls',
  'csv',
  'odt',
  'odp',
  'ods',
  'pdf',
  'dot',
  'rtf',
  'txt'
];

/**
 * Accepted extensions for workflow agent knowledge uploads. Kept separate from
 * the assistant list so workflow knowledge can diverge from the assistant's
 * local-file picker without changing assistant behaviour.
 */
export const VALID_WORKFLOW_KNOWLEDGE_FILE_EXTENSIONS = [
  'docx',
  'doc',
  'pptx',
  'ppt',
  'xlsx',
  'xls',
  'csv',
  'odt',
  'odp',
  'ods',
  'pdf',
  'dot',
  'rtf',
  'txt'
];

// Same list as an HTML accept= string for the file picker.
export const VALID_WORKFLOW_KNOWLEDGE_FILES_EXTENSION =
  '.docx, .doc, .pptx, .ppt, .xlsx, .xls, .csv, .odt, .odp, .ods, .pdf, .dot, .rtf, .txt';

// Extensions DataLibrary's dropzone accepts. Size limits are resolved
// centrally via src/utils/files/fileSizeLimits.ts based on the individual
// file's extension, inside the upload hooks that enforce validation — no
// document/media/spreadsheet grouping is needed here.
export const ALLOWED_DATA_LIBRARY_EXTENSIONS = [
  'pdf',
  'ppt',
  'pptx',
  'doc',
  'docx',
  'ods',
  'odp',
  'odt',
  'rtf',
  'dot',
  'mp3',
  'mp4',
  'mpga',
  'mpeg',
  'wav',
  'm4a',
  'webm',
  'mov',
  'm4v',
  '3gp',
  'aac',
  'caf',
  'amr',
  'xls',
  'xlsx',
  'csv',
  'txt'
];

export const MAX_CHAT_UPLOAD_FILES = 20;
export const CHAT_PRE_CREATION_ID = 0;
export const NEW_CHAT_ROUTE = 'chat';

export const SELECTED_DOC = 'selectedDoc';
export const ATLASSIAN_ACCOUNT_ID = 'userAtlassianAccountId';
export const USER_ATLASSIAN_PERMISSION = 'userAtlassianPermission';
export const ATLASSIAN_ACCESS_TOKEN = 'atlassianAccessToken';
export const ATLASSIAN_REFRESH_TOKEN = 'atlassianRefreshToken';
export const ATLASSIAN_BASE_AUTH_URL =
  'https://auth.atlassian.com/authorize?audience=api.atlassian.com';
export const ATLASSIAN_CONTENT_IMPORT_SCOPE =
  'scope=read:confluence-content.summary read:confluence-space.summary read:confluence-content.all offline_access';
export const ATLASSIAN_AUTH_RESPONSE_TYPE = 'code';
export const ATLASSIAN_EXCHANGE_CODE_URL =
  'https://auth.atlassian.com/oauth/token';
export const ATLASSIAN_AUTH_INTERVAL_TIME = 500;
export const ROOT_FOLDER_TYPES = {
  MY: 'my',
  SHARED: 'shared'
};
export const STATUS_ROUTE = 'status/';

export const CONTENT_PADDING = '24px';
export const UPLOADED_FILE_IDS = 'uploaded_file_ids';
export const ADMIN_USERS = ['admin', 'adminqa'];
// Resolves (via applicationsRoutes in router/dashboards.tsx) to the first
// feature the current role actually has access to - do not hardcode '/chat'
// here, it isn't guaranteed to be a registered route for every role. Must
// stay off of PUBLIC_ROUTES: MTP deployments reserve '/' as a public route
// matched before auth state is checked, so redirecting an authenticated user
// there causes an infinite PublicRoute reload loop.
export const DEFAULT_APPLICATION_ROUTE = '/home';
export const LANDING_PAGE = '/';
export const DOC_ANALYST_ONBOARDING_STEPS = [
  'Analysis Detail',
  'Create Questionnaire',
  'Create Dataset'
];

export const CANVAS_TAGS = {
  SELECTED_TEXT_OPEN: '<selected-text>',
  SELECTED_TEXT_CLOSE: '</selected-text>',
  CANVAS_OPEN: '<canvas>',
  CANVAS_CLOSE: '</canvas>',
  ADDITIONAL_METADATA_OPEN: '<additional_metadata>',
  ADDITIONAL_METADATA_CLOSE: '</additional_metadata>'
};

export const NAVIGATIONS = {
  CHAT: '/chat',
  DATA_LIBRARY: '/data-library',
  UPLOAD_SESSIONS: '/data-library/upload-sessions',
  PROJECTS: '/projects',
  APPS: '/apps',
  CONNECTORS: '/connectors',
  SKILLS: '/skills',
  ASSISTANTS: '/assistants',
  SETTINGS_PAY_AS_YOU_GO: '/settings/pay-as-you-go',
  SETTINGS_BILLING: '/settings/billing',
  WORKFLOWS: '/workflows',
  ADVANCE_SEARCH: '/advanced-search',
  ANALYTICS: ANALYTICS_ROUTES
};

export const ADMIN_CONSOLE_ROUTES = {
  TENANTS: 'admin-console/tenants',
  MODELS: 'admin-console/models',
  USAGE: '/usage',
  NOTIFICATIONS: 'admin-console/notifications',
  ORG_NOTIFICATIONS: 'admin-console/organization-notifications'
};
export type FileEncodingType = 'binary' | 'base64' | undefined;

export type UploadStatus =
  | 'idle'
  | 'uploading'
  | 'uploaded'
  | 'failed'
  | 'preparing'
  | 'dropped'
  | 'queued';

export interface UploadFile {
  id: string;
  file: File;
  name: string;
  size: number;
  progress: number; // 0–100
  status: UploadStatus;
  error?: string;
  uploadType?: UPLOAD_TYPE;
}
export const NANO_BANANA_TOOL_DISPLAY_NAME = 'Nano Banana';
export const BILLING_ROUTE = '/settings/billing';
export const MAX_RETRIES_PUBSUB = 5;
export const REGION_ICON_MAP = {
  eu: EURegion,
  us: USRegion,
  global: GlobalRegion
};
export const REGION_GLOBAL = 'global';

export const ADMIN_ACCOUNT_TYPES: ACCOUNT_TYPE[] = [
  ACCOUNT_TYPE.ORGANIZATION_ADMINISTRATOR,
  ACCOUNT_TYPE.ORGANIZATION_OWNER
];

export const PREMIUM_FEATURE_CREDITS = {
  WORKFLOW: 'workflow_credits'
};

export const MODEL_CONFIG_PAGE_ROUTE = '/settings/model-configuration';

export const Z_INDEX = {
  /** Radix dialog opened from within a MUI dialog. */
  NESTED_DIALOG: 1600,
  /** Popover/dropdown content rendered inside a NESTED_DIALOG (e.g. Advanced Search) must sit above it. */
  ABOVE_NESTED_DIALOG: 1650
} as const;

/**
 * Built-in organization user groups. Their access to a resource can never be
 * revoked, so sharing UIs must not offer a "remove" action for them.
 */
export const ORGANIZATION_ADMINISTRATOR = 'Organization Administrators';
export const ORGANIZATION_OWNERS = 'Organization Owners';
